/// <amd-module name="Widgets/SprintOverviewWidget/SprintOverviewDataProvider" />
import { getWorkClient, IWorkRestClient } from "Dashboards/Clients/Work";
import { WorkItemTrackingClientName } from "Widgets/SprintOverviewWidget/clients/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Widgets/SprintOverviewWidget/clients/WorkItemTracking.Types";

import { getNumberOfWorkingDays } from "Agile/Common/Utils/CapacityDateUtils";
import { adjustDateRangeFromServer, getUtcDate } from "Agile/Common/Utils/Date";
import {
    BacklogConfiguration,
    IterationWorkItems,
    ProcessConfiguration,
    TeamSetting,
    TeamSettingsIteration,
    WorkItemTypeStateInfo
} from "Dashboards/Clients/Contracts/Work";
import { WorkItem, WorkItemErrorPolicy, WorkItemExpand } from "Dashboards/Clients/Contracts/WorkItemTracking";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { IVssPageContext } from "VSS/Platform/Context";

export interface IterationInfo {
    teamSettings: TeamSetting;
    backlogConfiguration: BacklogConfiguration;
    processConfiguration: ProcessConfiguration;
}

export interface WorkItemRollupValue {
    count: number;
    effort: number;
}

export interface IterationTimeFrame {
    totalDaysInIteration: number;
    totalWorkDaysInIteration: number;

    remainingDaysInIteration: number;
    remainingWorkDaysInIteration: number;
}

export interface WorkItemStatusRollup {
    notStarted: WorkItemRollupValue;
    inProgress: WorkItemRollupValue;
    complete: WorkItemRollupValue;
}

const daysInAWeek = [1, 2, 3, 4, 5, 6, 7];

export async function getIterationInfo(context: IVssPageContext, teamContext: TeamContext): Promise<IterationInfo> {
    const workClient: IWorkRestClient = getWorkClient(context);
    const [teamSettings, iterations, backlogConfiguration, processConfiguration] = await Promise.all([
        workClient.getTeamSettings(teamContext),
        workClient.getTeamIterations(teamContext, "Current"),
        workClient.getBacklogConfigurations(teamContext),
        workClient.getProcessConfiguration(teamContext.projectId)
    ]);
    teamSettings.defaultIteration = iterations[0]; // The original value doesnt have startdate and enddate.
    return {
        teamSettings,
        backlogConfiguration,
        processConfiguration
    };
}

export async function getIterationWorkItemIds(
    context: IVssPageContext,
    teamContext: TeamContext,
    iteration: TeamSettingsIteration
): Promise<IterationWorkItems> {
    const workClient: IWorkRestClient = getWorkClient(context);
    return workClient.getIterationWorkItems(teamContext, iteration.id);
}

export async function getIterationTimeframe(
    context: IVssPageContext,
    teamContext: TeamContext,
    iteration: TeamSettingsIteration,
    workingDays: number[],
    currentDate?: Date
): Promise<IterationTimeFrame> {
    const workClient: IWorkRestClient = getWorkClient(context);
    const teamDaysOff = await workClient.getTeamDaysOff(teamContext, iteration.id);
    const daysOffRange = shiftDateRanges(teamDaysOff.daysOff);
    const workingDaysSet = new Set();
    workingDays.map(x => workingDaysSet.add(x));
    const weekends = daysInAWeek.filter(x => !workingDaysSet.has(x));

    if (weekends.indexOf(7) !== -1) {
        // Convert to 0 based indexing
        const sundayIndex = weekends.indexOf(7);
        weekends.splice(sundayIndex, 1, 0);
    }

    // Injection is allowed for testing purposes
    if (!currentDate) {
        currentDate = new Date();
    }
    // getNumberOfWorkingDays has a documentation flaw. Comparison of dates is done on date objects, not on date.getDate value.
    currentDate.setHours(0);
    currentDate.setMinutes(0);
    currentDate.setSeconds(0);
    currentDate.setMilliseconds(0);

    // getNumberOfWorkingDays does not correct for utc offset and can return incorrect info
    // when either date is a monday or friday.
    const iterationStartDate = getUtcDate(iteration.attributes.startDate);
    const iterationFinishDate = getUtcDate(iteration.attributes.finishDate);

    const result = {
        remainingWorkDaysInIteration: getNumberOfWorkingDays(currentDate, iterationFinishDate, weekends, daysOffRange),
        totalWorkDaysInIteration: getNumberOfWorkingDays(iterationStartDate, iterationFinishDate, weekends, daysOffRange),

        remainingDaysInIteration: getNumberOfDays(currentDate, iterationFinishDate),
        totalDaysInIteration: getNumberOfDays(iterationStartDate, iterationFinishDate)
    };
    return result;
}

/**
 * Compute the number of days occupied from start to end of two dates, inclusive of first moment on start date to last moment of end date.
 * Currently expects pre-normalized dates aligned to same clock time.
 * @param startDate - the start date
 * @param endDate - The end date
 */
function getNumberOfDays(startDate: Date, endDate: Date): number {
    return Math.ceil(Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

export async function getIterationWorkRollup(
    context: IVssPageContext,
    workItemRefs: number[],
    backlogConfiguration: BacklogConfiguration,
    processConfiguration: ProcessConfiguration
): Promise<WorkItemStatusRollup> {
    const effortFieldReferenceName: string = processConfiguration.typeFields["Effort"].referenceName;
    const actualWorkItems = workItemRefs.length ? await getWorkItems(workItemRefs, effortFieldReferenceName) : [];
    const requirementsTypes: string[] = backlogConfiguration.requirementBacklog.workItemTypes.map(x => x.name);
    const taskTypes: string[] = backlogConfiguration.taskBacklog.workItemTypes.map(x => x.name);
    const combinedWorkItemTypes: string[] = [...requirementsTypes, ...taskTypes];
    const filteredWorkItems = actualWorkItems.filter(x => combinedWorkItemTypes.indexOf(x.fields["System.WorkItemType"]) !== -1);

    // Convert to a string dictionary to string dictionary to string
    const workItemsStateMap: { [key: string]: { [key: string]: string } } = backlogConfiguration.workItemTypeMappedStates.reduce(function(
        map: { [key: string]: { [key: string]: string } },
        typeMapping: WorkItemTypeStateInfo
    ) {
        map[typeMapping.workItemTypeName] = typeMapping.states;
        return map;
    },
    {});

    const closed: WorkItemRollupValue = { count: 0, effort: 0 };
    const active: WorkItemRollupValue = { count: 0, effort: 0 };
    const newItems: WorkItemRollupValue = { count: 0, effort: 0 };
    filteredWorkItems.forEach(workItem => {
        const type: string = workItem.fields["System.WorkItemType"];
        const typeStateMap = workItemsStateMap[type];
        const state = typeStateMap[workItem.fields["System.State"]];

        if (state === "Proposed") {
            newItems.count++;
            newItems.effort += workItem.fields[effortFieldReferenceName] || 0;
        } else if (state === "InProgress" || state === "Resolved") {
            active.count++;
            active.effort += workItem.fields[effortFieldReferenceName] || 0;
        } else if (state === "Completed") {
            closed.count++;
            closed.effort += workItem.fields[effortFieldReferenceName] || 0;
        }
    });
    return {
        complete: closed,
        inProgress: active,
        notStarted: newItems
    };

    async function getWorkItems(workItemRefs: number[], effortFieldReferenceName: string): Promise<WorkItem[]> {
        const pagesize = 200; // we need this paging mechanism as the REST API has a limit of 200 work items per request
        const allPromises = [];
        for (let i = 0; i < workItemRefs.length; i += pagesize) {
            const currentPageWorkItemIds = workItemRefs.slice(i, i + pagesize);
            allPromises.push(makeWorkItemsRequest(currentPageWorkItemIds, effortFieldReferenceName));
        }
        const allPromisesResolved = await Promise.all(allPromises);
        return ([] as WorkItem[]).concat(...allPromisesResolved);
    }

    async function makeWorkItemsRequest(workItemIds: number[], effortFieldReferenceName: string): Promise<WorkItem[]> {
        const workItemClient: IWorkItemTrackingRestClient = context.getRestClient<IWorkItemTrackingRestClient>(WorkItemTrackingClientName);

        return workItemClient.getWorkItemsBatch({
            ids: workItemIds,
            fields: ["System.WorkItemType", "System.State", effortFieldReferenceName],
            $expand: WorkItemExpand.None,
            asOf: new Date(),
            errorPolicy: WorkItemErrorPolicy.Omit
        });
    }
}

function shiftDateRanges(dateRanges: any[]): any[] {
    return dateRanges.map(dateRange => {
        return adjustDateRangeFromServer(dateRange);
    });
}
